// 作品文本导出：TXT / Markdown 格式（客户端生成并下载，不经过服务端）
import type { Book } from '../types';
import { numberToChinese } from './numberToChinese';
import JSZip from 'jszip';
import { Document, Packer, Paragraph, HeadingLevel, AlignmentType } from 'docx';
import { encodeText, mimeFor, type ExportEncoding } from './textEncoding';
import {
  DEFAULT_PRESET, EXPORT_PRESETS, applyTemplate, formatBody, type ExportPreset,
} from './exportPresets';

// 预设定义集中在 exportPresets.ts（纯逻辑，可单测）；此处重导出以保持调用方不变
export { DEFAULT_PRESET, EXPORT_PRESETS };
export type { ExportPreset };

function stripHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 触发下载。宿主明确禁止下载时抛错，让调用方如实告知用户
 * （DSH 内嵌浏览器会在 will-download 里 preventDefault，此时点 a.click()
 * 不报错也没有任何提示，文件不会落盘）。
 */
function downloadBytes(filename: string, bytes: Uint8Array, mime: string): void {
  if ((window as unknown as { __DISABLE_DOWNLOAD__?: boolean }).__DISABLE_DOWNLOAD__ === true) {
    throw new Error('DOWNLOAD_BLOCKED');
  }
  const blob = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // 延迟释放：宿主可能异步读取 blob
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function safeFilename(name: string): string {
  return (name || '未命名作品').replace(/[\\/:*?"<>|]/g, '_').trim() || '未命名作品';
}

export interface TextExportOptions {
  /** 目标编码；默认 UTF-8（带 BOM） */
  encoding?: ExportEncoding;
  /** 导出预设；缺省用默认预设 */
  preset?: ExportPreset;
}

export interface TextExportResult {
  success: boolean;
  error?: string;
  /** 因目标编码无法表示而被降级为字符引用的字符数（GBK 导出时可能 > 0） */
  replacedCount?: number;
  /** 被降级字符的去重样例 */
  replacedSamples?: string[];
}

export async function exportBookAsText(book: Book, format: 'txt' | 'md', options?: TextExportOptions): Promise<TextExportResult> {
  try {
    const encoding: ExportEncoding = options?.encoding ?? 'utf8';
    const preset = options?.preset ?? DEFAULT_PRESET;
    const volumes = await window.api.getVolumes(book.id);
    const sortedVols = [...volumes].sort((a, b) => a.order_index - b.order_index);

    const lines: string[] = [];
    const add = (s: string) => lines.push(s);

    if (format === 'md') {
      add(`# ${book.title}`);
      if (book.description) add(`\n> ${book.description.replace(/\n/g, '\n> ')}`);
    } else {
      add(book.title);
      if (book.description) add(book.description);
      add('='.repeat(24));
    }

    for (const vol of sortedVols) {
      const volIndex = sortedVols.indexOf(vol) + 1;
      const chapters = await window.api.getChapters(vol.id);
      const sortedChaps = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);
      const volTitle = applyTemplate(preset.volumeTitleFormat, {
        n: numberToChinese(volIndex),
        title: vol.title || '',
      });
      if (format === 'md') {
        add('');
        add(`## ${volTitle}`);
      } else {
        add('');
        add(`【${volTitle}】`);
      }

      for (const ch of sortedChaps) {
        const content = await window.api.getChapterContent(ch.id).catch(() => '');
        const chTitle = applyTemplate(preset.chapterTitleFormat, {
          n: numberToChinese(ch.chapter_number),
          title: ch.title,
        });
        if (format === 'md') {
          add('');
          add(`### ${chTitle}`);
          if (preset.includeSummary && ch.summary) add(`> ${ch.summary.replace(/\n/g, '\n> ')}`);
        } else {
          add('');
          add(chTitle);
          if (preset.includeSummary && ch.summary) add(`（${ch.summary}）`);
        }
        const text = stripHtml(content);
        if (text) add(formatBody(text, preset));
      }
    }

    const fullText = lines.join('\n');
    const baseName = safeFilename(book.title);
    const extension = format === 'md' ? 'md' : 'txt';
    // 编码写入文件名，避免用户拿到文件后分不清是 UTF-8 还是 GBK
    const suffix = encoding === 'gbk' ? '-gbk' : '';
    const { bytes, replacedCount, replacedSamples } = encodeText(fullText, encoding);
    downloadBytes(`${baseName}${suffix}.${extension}`, bytes, mimeFor(encoding));
    return { success: true, replacedCount, replacedSamples };
  } catch (error) {
    return { success: false, error: String(error) };
  }
}

// ── EPUB 导出（客户端生成，含目录） ─────────────────────────────────

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/** 将 TipTap HTML 转换为干净的 XHTML（仅保留常用块级/行内标签） */
function htmlToXhtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  const clean = (node: Element): string => {
    const tag = node.tagName.toLowerCase();
    const allowed = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'ul', 'ol', 'li', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'span'];
    if (!allowed.includes(tag)) {
      return Array.from(node.childNodes)
        .map((c) => c.nodeType === 3 ? escapeXml(c.textContent ?? '') : c instanceof Element ? clean(c) : '')
        .join('');
    }
    const inner = Array.from(node.childNodes)
      .map((c) => c.nodeType === 3 ? escapeXml(c.textContent ?? '') : c instanceof Element ? clean(c) : '')
      .join('');
    if (tag === 'br') return '<br/>';
    if (tag === 'hr') return '<hr/>';
    return `<${tag}>${inner}</${tag}>`;
  };
  return Array.from(div.childNodes)
    .map((c) => c.nodeType === 3 ? escapeXml(c.textContent ?? '') : c instanceof Element ? clean(c) : '')
    .join('');
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportBookAsEpub(book: Book): Promise<{ success: boolean; error?: string }> {
  try {
    const volumes = await window.api.getVolumes(book.id);
    const sortedVols = [...volumes].sort((a, b) => a.order_index - b.order_index);

    const chapters: Array<{ id: string; number: number; title: string; html: string }> = [];
    for (const vol of sortedVols) {
      const chaps = await window.api.getChapters(vol.id);
      const sortedChaps = [...chaps].sort((a, b) => a.chapter_number - b.chapter_number);
      for (const ch of sortedChaps) {
        const content = await window.api.getChapterContent(ch.id).catch(() => '');
        chapters.push({ id: ch.id, number: ch.chapter_number ?? 0, title: ch.title, html: htmlToXhtml(content) });
      }
    }

    const zip = new JSZip();
    zip.file('mimetype', 'application/epub+zip', { compression: 'STORE' });
    zip.file('META-INF/container.xml', `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles><rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/></rootfiles>
</container>`);
    zip.file('OEBPS/style.css', `body { font-family: serif; line-height: 1.8; margin: 1em; }
p { text-indent: 2em; margin: 0 0 0.5em 0; }
h1, h2, h3 { text-indent: 0; margin: 1em 0 0.5em; }
blockquote { margin: 0.8em 2em; font-style: italic; }`);

    const manifest = [
      `<item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>`,
      `<item id="css" href="style.css" media-type="text/css"/>`,
      ...chapters.map((c, i) => `<item id="ch${i + 1}" href="ch${i + 1}.xhtml" media-type="application/xhtml+xml"/>`),
    ].join('\n    ');
    const spine = chapters.map((c, i) => `<itemref idref="ch${i + 1}"/>`).join('\n    ');
    const navPoints = chapters.map((c, i) =>
      `    <navPoint id="np${i + 1}" playOrder="${i + 1}"><navLabel><text>第${numberToChinese(c.number)}章 ${escapeXml(c.title)}</text></navLabel><content src="ch${i + 1}.xhtml"/></navPoint>`,
    ).join('\n');

    zip.file('OEBPS/content.opf', `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="2.0" unique-identifier="bookid">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:title>${escapeXml(book.title)}</dc:title>
    <dc:language>zh-CN</dc:language>
    <dc:identifier id="bookid">${book.id}</dc:identifier>
    <dc:creator>小天书</dc:creator>
  </metadata>
  <manifest>
    ${manifest}
  </manifest>
  <spine toc="ncx">
    ${spine}
  </spine>
</package>`);
    zip.file('OEBPS/toc.ncx', `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head><meta name="dtb:uid" content="${book.id}"/></head>
  <docTitle><text>${escapeXml(book.title)}</text></docTitle>
  <navMap>
${navPoints}
  </navMap>
</ncx>`);

    chapters.forEach((c, i) => {
      zip.file(`OEBPS/ch${i + 1}.xhtml`, `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
<head><title>第${numberToChinese(c.number)}章 ${escapeXml(c.title)}</title><link rel="stylesheet" type="text/css" href="style.css"/></head>
<body><h2>第${numberToChinese(c.number)}章 ${escapeXml(c.title)}</h2>${c.html || '<p></p>'}</body>
</html>`);
    });

    const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/epub+zip' });
    downloadBlob(`${safeFilename(book.title)}.epub`, blob);
    return { success: true };
  } catch (error) {
    return { success: false, error: String(error) };
  }
}

// ── Word (.docx) 导出 ───────────────────────────────────────────────

function htmlToDocxParagraphs(html: string): Paragraph[] {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  const paras: Paragraph[] = [];
  div.querySelectorAll('p, h1, h2, h3, h4, h5, h6, blockquote, li').forEach((el) => {
    const text = (el.textContent ?? '').trim();
    if (!text) return;
    const tag = el.tagName.toLowerCase();
    if (tag.startsWith('h')) {
      paras.push(new Paragraph({
        text,
        heading: tag === 'h1' ? HeadingLevel.HEADING_1 : tag === 'h2' ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
      }));
    } else if (tag === 'blockquote') {
      paras.push(new Paragraph({ text, indent: { left: 400 }, style: 'Quote' }));
    } else if (tag === 'li') {
      paras.push(new Paragraph({ text: `• ${text}`, indent: { left: 400 } }));
    } else {
      paras.push(new Paragraph({ text }));
    }
  });
  return paras;
}

export async function exportBookAsDocx(book: Book): Promise<{ success: boolean; error?: string }> {
  try {
    const volumes = await window.api.getVolumes(book.id);
    const sortedVols = [...volumes].sort((a, b) => a.order_index - b.order_index);

    const children: Paragraph[] = [
      new Paragraph({ text: book.title, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
      ...(book.description ? [new Paragraph({ text: book.description, alignment: AlignmentType.CENTER })] : []),
    ];
    for (const vol of sortedVols) {
      children.push(new Paragraph({ text: `第${numberToChinese(sortedVols.indexOf(vol) + 1)}卷 ${vol.title || ''}`.replace(/\s+$/, ''), heading: HeadingLevel.HEADING_1 }));
      const chaps = await window.api.getChapters(vol.id);
      const sortedChaps = [...chaps].sort((a, b) => a.chapter_number - b.chapter_number);
      for (const ch of sortedChaps) {
        children.push(new Paragraph({ text: `第${numberToChinese(ch.chapter_number)}章 ${ch.title}`, heading: HeadingLevel.HEADING_2 }));
        const content = await window.api.getChapterContent(ch.id).catch(() => '');
        children.push(...htmlToDocxParagraphs(content));
      }
    }

    const doc = new Document({
      styles: {
        default: { document: { run: { font: '宋体', size: 24 } } },
      },
      sections: [{ children }],
    });
    const blob = await Packer.toBlob(doc);
    downloadBlob(`${safeFilename(book.title)}.docx`, blob);
    return { success: true };
  } catch (error) {
    return { success: false, error: String(error) };
  }
}
